import { rfidTagApi } from "#/api/rfidTagApi";
import { showSuccessNotification } from "#/components/notifications";
import type { RfidTag } from "#/types/rfidTag";
import { Button, Modal } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { logger } from "#/utils/logger";

interface DeleteRfidTagModalProps {
  opened: boolean;
  onClose: () => void;
  rfidTag: RfidTag | undefined;
  onSuccess: () => void;
}

const DeleteRfidTagModal = ({
  opened,
  onClose,
  rfidTag,
  onSuccess,
}: DeleteRfidTagModalProps) => {
  const { t } = useTranslation();
  const { canDelete } = usePermission();
  const deleteRfidTagMutation = useMutation({
    mutationFn: async (id: number) => {
      return await rfidTagApi.delete(id);
    },
    onSuccess: () => {
      showSuccessNotification({
        message: t("rfidTags.deleteTag.success"),
      });
      onClose();
      onSuccess();
    },
    onError: (error) => {
      logger.error("DeleteRfidTagModal deleteRfidTag error:", error);
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={t("rfidTags.deleteTag.title")}
      closeOnClickOutside={false}
    >
      <p>
        {t("rfidTags.deleteTag.confirmation", { rfid: "" })}{" "}
        <strong className="font-mono">{rfidTag?.rfid}</strong>?
      </p>
      <div className="flex items-center justify-end gap-2 mt-4">
        <Button variant="outline" color="black" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <PermissionTooltip disabled={!canDelete()}>
          <Button
            color="red"
            leftSection={<IconTrash size={18} />}
            onClick={() => {
              if (rfidTag) {
                deleteRfidTagMutation.mutate(rfidTag.id);
              }
            }}
            loading={deleteRfidTagMutation.isPending}
            disabled={!canDelete()}
          >
            {t("common.delete")}
          </Button>
        </PermissionTooltip>
      </div>
    </Modal>
  );
};

export default DeleteRfidTagModal;
